import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/components/PageHeader";
import { RiskBadge } from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { useUI } from "@/context/UIContext";
import { getKnowledgeItem } from "@/lib/api";
import { CATEGORY_LABELS, riskTone, depTone } from "@/lib/format";
import { Plus, ShieldAlert, Info, Pill, Clock, Activity } from "lucide-react";

function Section({ title, items, icon: Icon }) {
  if (!items || !items.length) return null;
  return (
    <div className="card-soft p-4">
      <div className="flex items-center gap-2 mb-2">{Icon && <Icon className="h-4 w-4 text-primary" />}<p className="font-semibold text-sm">{title}</p></div>
      <ul className="list-disc pl-4 space-y-1 text-sm text-muted-foreground">{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  );
}

export default function KnowledgeArticle() {
  const { id } = useParams();
  const ui = useUI();
  const { data: it, isLoading } = useQuery({ queryKey: ["knowledge-item", id], queryFn: () => getKnowledgeItem(id) });
  if (isLoading || !it) return <div><PageHeader back title="" /><div className="px-4 h-40 rounded-2xl bg-muted/60 animate-pulse" /></div>;

  return (
    <div>
      <PageHeader back title={it.name} subtitle={it.drug_class} />
      <div className="px-4 space-y-4">
        <div className="card-soft p-4">
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[11px] rounded-full bg-muted px-2 py-0.5 text-muted-foreground">{CATEGORY_LABELS[it.category] || it.category}</span>
            <RiskBadge tone={riskTone(it.risk_level)} label={`${it.risk_level} risk`} />
            {depTone(it.dependency_risk_category) && <RiskBadge tone="dependency" label={`${it.dependency_risk_category} dependency`} icon={false} />}
          </div>
          <p className="text-[15px] leading-7 mt-3">{it.content}</p>
          {it.brand_names?.length > 0 && <p className="text-xs text-muted-foreground mt-2">Brand names: {it.brand_names.join(", ")}</p>}
          {it.street_names?.length > 0 && <p className="text-xs text-muted-foreground mt-2">Also known as: {it.street_names.join(", ")}</p>}
          <Button className="w-full rounded-xl mt-4" onClick={() => ui.openAddMed(it)} data-testid="kb-add-to-meds"><Plus className="h-4 w-4 mr-1" />Add to my medications</Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {it.typical_dosing && <div className="card-soft p-4"><div className="flex items-center gap-2 mb-1"><Pill className="h-4 w-4 text-primary" /><p className="text-xs text-muted-foreground">Typical dosing</p></div><p className="text-sm font-medium">{it.typical_dosing}</p></div>}
          {it.half_life && <div className="card-soft p-4"><div className="flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-primary" /><p className="text-xs text-muted-foreground">Half-life</p></div><p className="text-sm font-medium">{it.half_life}</p></div>}
        </div>

        {it.mechanism && <div className="card-soft p-4"><div className="flex items-center gap-2 mb-1"><Activity className="h-4 w-4 text-primary" /><p className="font-semibold text-sm">How it works</p></div><p className="text-sm text-muted-foreground">{it.mechanism}</p></div>}

        <Section title="Common side effects" items={it.common_side_effects} icon={Info} />
        <Section title="Serious side effects" items={it.serious_side_effects} icon={ShieldAlert} />
        <Section title="Interactions" items={it.interactions} icon={Info} />
        <Section title="Warnings" items={it.warnings} icon={ShieldAlert} />

        <div className="rounded-2xl bg-muted/50 border border-border p-4 text-xs text-muted-foreground">
          {it.source === "ai" ? "This article was generated by AI from general medical knowledge. " : ""}
          Educational information only — not medical advice. Confirm details with a clinician or pharmacist.
        </div>
      </div>
    </div>
  );
}
